import { useEffect, useState } from 'react'
import { fetchVocabularyStats } from '@/api/vocabulary'
import type { VocabularyStats } from '@/types/vocabulary'

// In-memory module cache (D12). No TTL — invalidated only when the learner
// tracks a word (trackWord calls invalidateVocabularyStats on success).
const cache = new Map<number, VocabularyStats>()

export function invalidateVocabularyStats(languageId: number): void {
  cache.delete(languageId)
}

interface UseVocabularyStats {
  stats: VocabularyStats | null
  loading: boolean
  error: string | null
}

export function useVocabularyStats(languageId: number): UseVocabularyStats {
  const cached = cache.get(languageId) ?? null
  const [stats, setStats] = useState<VocabularyStats | null>(cached)
  const [loading, setLoading] = useState(cached === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const hit = cache.get(languageId)
    if (hit) {
      setStats(hit)
      setLoading(false)
      setError(null)
      return
    }

    let active = true
    setLoading(true)
    setError(null)
    fetchVocabularyStats(languageId)
      .then((res) => {
        if (!active) return
        cache.set(languageId, res.data.data)
        setStats(res.data.data)
      })
      .catch(() => {
        if (active) setError('Could not load vocabulary stats.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [languageId])

  return { stats, loading, error }
}
